import { privateRequest } from "../axios";
import type { ApiResponse, ListApiResponse } from "@/types/common";

// Confirmed real endpoint and response shape (`GET /comment-reports`),
// given directly by the user via a live sample response — see
// docs/decisions.md #91. An earlier guessed shape (a flat one-row-per-
// report list) was wrong in several ways once the real sample arrived:
// it's one row per flagged *comment* instead, with a nested `reports`
// array (a comment can be reported more than once, by different
// members), and the commenter's name comes back as one combined
// `commentAuthorName` string, not separate first/last name fields.
export interface FlaggedCommentReport {
  reportId: number;
  reportedByName: string;
  reportedByEmail: string;
  reasonName: string;
  createdAt: string;
}

export interface FlaggedComment {
  commentId: number;
  commentAuthorName: string;
  eventName: string;
  commentText: string;
  reportCount: number;
  reports: FlaggedCommentReport[];
}

export interface GetCommentReportsParams {
  pageNo?: number;
  perPage?: number;
}

const ENDPOINTS = {
  COMMENT_REPORTS: "/comment-reports",
};

const getCommentReports = async (params?: GetCommentReportsParams) => {
  const response = await privateRequest.get<ListApiResponse<FlaggedComment>>(ENDPOINTS.COMMENT_REPORTS, { params });
  return response.data;
};

// Nested under `/comment-reports` — corrected by the user (the first
// pass, before that correction, tried these as bare paths at the API
// root; see docs/decisions.md #91/#92).
const hideComment = async (commentId: number) => {
  const response = await privateRequest.put<ApiResponse<null>>(`${ENDPOINTS.COMMENT_REPORTS}/${commentId}/hide`);
  return response.data;
};

const dismissReport = async (reportId: number) => {
  const response = await privateRequest.put<ApiResponse<null>>(`${ENDPOINTS.COMMENT_REPORTS}/${reportId}/dismiss`);
  return response.data;
};

const commentReportService = {
  getCommentReports,
  hideComment,
  dismissReport,
};

export default commentReportService;
